import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import dayjs from "dayjs";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { UseQueryResult } from "react-query";

import APFilterableList from "../../components/APFilterableList";
import APContactHelpButton from "../../components/buttons/APContactHelpButton";
import BackButton from "../../components/buttons/BackButton";
import { APCardColor } from "../../components/cards/APCard";
import APAutoComplete, {
  AutoCompleteOption,
} from "../../components/form/APAutoComplete";
import APDateField from "../../components/form/APDateField";
import APTextField from "../../components/form/APTextField";
import RestrictedAppScope from "../../components/RestrictedAppScope";
import { buildAutoCompleteOptions } from "../../helpers/buildOptions";
import { openInNewTab } from "../../helpers/routes";
import {
  DocumentPageName,
  getDocumentTypesForDocumentPage,
} from "../../helpers/user";
import { APQueryOptions } from "../../providers/APQueryProvider";
import { DocumentFilters } from "../../types/Document";
import { APDocument, APDocumentType } from "../../types/Document";
import { PaginationResponse } from "../../types/Pagination";
import DocumentCard from "../components/DocumentCard";

type DocumentListConfig = {
  title: string;
  filters: (keyof DocumentFilterInputs)[];
  titleFields: (keyof APDocument)[];
  bodyFields: (keyof APDocument)[][];
  expandFields: (keyof APDocument)[][];
  filterLabels?: Partial<Record<keyof APDocument, string>>;
  fieldLabels?: Partial<Record<keyof APDocument, string>>;
};

const defaultConfig: Omit<DocumentListConfig, "title"> = {
  filters: ["accounts", "dateFrom", "dateTo", "name", "documentTypes"],
  titleFields: ["name", "documentType"],
  bodyFields: [["date"]],
  expandFields: [["accountName"], ["externalId"], ["note"]],
};

const documentListConfigMap: Record<DocumentPageName, DocumentListConfig> = {
  employerInvoices: {
    title: "Invoice",
    filters: [
      "accounts",
      "dateFrom",
      "dateTo",
      "date2From",
      "date2To",
      "name",
      "documentTypes",
    ],
    titleFields: ["externalId"],
    bodyFields: [["date"], ["date2"]],
    expandFields: [["accountName"], ["name", "documentType"], ["note"]],
    filterLabels: {
      date: "Invoice Date",
      date2: "Due Date",
    },
    fieldLabels: {
      date: "Invoice",
      date2: "Due",
    },
  },
  employerPackagingAgreements: {
    title: "Packaging Agreements",
    ...defaultConfig,
  },
  employerPayrollAdvice: {
    title: "Payroll Advice",
    ...defaultConfig,
  },
  employerFBTReports: {
    title: "FBT Reports",
    ...defaultConfig,
  },
  employerEngagementReports: {
    title: "Engagement",
    ...defaultConfig,
  },
  employerLuxuryLeaseReports: {
    title: "Luxury Lease",
    ...defaultConfig,
  },
  employerPromotions: { title: "Promotions", ...defaultConfig },
  dossierDocuments: {
    title: "Documents",
    ...defaultConfig,
  },
};

type DocumentFilterInputs = {
  accounts: AutoCompleteOption[] | null;
  payrollCycle: AutoCompleteOption | null;
  dateFrom: string;
  dateTo: string;
  date2From: string;
  date2To: string;
  name: string;
  documentTypes: AutoCompleteOption[] | null;
};

export const DEFAULT_PAGE_SIZE = 10;

const accountColors: APCardColor[] = [
  "neutralWhite",
  "primary",
  "tertiaryGreen",
  "neutralGray",
  "tertiaryBlue",
  "tertiaryYellow",
  "tertiaryDarkBlue",
];

const DocumentList = ({
  page,
  getDocuments,
  onDownload,
  topCollapseContent,
  accountOptions,
  isLoading = false,
  isSuccess = true,
}: {
  page: DocumentPageName;
  getDocuments: (
    filters: DocumentFilters,
    options?: APQueryOptions
  ) => UseQueryResult<PaginationResponse<APDocument>, unknown>;
  onDownload: (documentId: string, fileName: string) => void;
  topCollapseContent?: React.ReactNode;
  accountOptions?: AutoCompleteOption[];
  isLoading?: boolean;
  isSuccess?: boolean;
}) => {
  const documentTypes = getDocumentTypesForDocumentPage(page);
  const [filters, setFilters] = useState<DocumentFilters>({
    pageSize: DEFAULT_PAGE_SIZE,
    documentTypes: documentTypes,
  });

  const [accountColorMap, setAccountColorMap] = useState<
    Record<string, APCardColor>
  >({});

  const {
    control,
    handleSubmit,
    setError,
    setValue,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm<DocumentFilterInputs>({
    mode: "onTouched",
  });

  const listConfig = documentListConfigMap[page];
  const dateFrom = watch("dateFrom");
  const dateTo = watch("dateTo");
  const date2From = watch("date2From");
  const date2To = watch("date2To");

  const updateAccountColorMap = (items: APDocument[]) => {
    const newColorMap = { ...accountColorMap };

    items.forEach((document) => {
      // If account does not already have a color then assign it one
      if (!newColorMap[document.accountId]) {
        const colorIndex = Object.keys(newColorMap).length % 7;
        newColorMap[document.accountId] = accountColors[colorIndex];
      }
    });

    if (
      Object.keys(newColorMap).length !== Object.keys(accountColorMap).length
    ) {
      setAccountColorMap(newColorMap);
    }
  };

  const buildFieldLabel = (fieldName: keyof APDocument, postfix: string) => {
    return `${
      listConfig.filterLabels?.[fieldName]
        ? `${listConfig.filterLabels[fieldName]} `
        : ""
    }${postfix}`;
  };

  const inputsToFilters = (inputs: DocumentFilterInputs) => {
    return {
      ...filters,
      nextPageLink: undefined,
      accountIds: inputs.accounts
        ? inputs.accounts.map((account) => account.id)
        : undefined,
      dateTo: inputs.dateTo
        ? dayjs(inputs.dateTo).endOf("day").toISOString()
        : undefined,
      dateFrom: inputs.dateFrom
        ? dayjs(inputs.dateFrom).toISOString()
        : undefined,
      date2To: inputs.date2To
        ? dayjs(inputs.date2To).endOf("day").toISOString()
        : undefined,
      date2From: inputs.date2From
        ? dayjs(inputs.date2From).toISOString()
        : undefined,
      nameSearch: inputs.name || undefined,
      documentTypes: inputs.documentTypes
        ? inputs.documentTypes.map((type) => type.id as APDocumentType)
        : documentTypes,
    };
  };

  const handleOpenUrl = (url: string) => {
    openInNewTab(url);
  };

  const renderDocumentCard = (document: APDocument) => {
    return (
      <DocumentCard
        key={document.id}
        document={document}
        onDownload={onDownload}
        onOpenUrl={handleOpenUrl}
        titleFields={listConfig.titleFields}
        bodyFields={listConfig.bodyFields}
        expandFields={listConfig.expandFields}
        fieldLabels={listConfig.fieldLabels}
        color={accountColorMap[document.accountId] || accountColorMap[0]}
      />
    );
  };

  const documentOptions = buildAutoCompleteOptions(
    documentTypes.map((type) => {
      return { type };
    }),
    "type",
    "type"
  );

  const filterKeys = listConfig.filters;

  const renderItems = (items: APDocument[]) => {
    return <>{items.map(renderDocumentCard)}</>;
  };

  return (
    <APFilterableList
      title={listConfig.title}
      filters={filters}
      setFilters={setFilters}
      pageSize={DEFAULT_PAGE_SIZE}
      getItems={getDocuments}
      inputsToFilters={inputsToFilters}
      handleSubmit={handleSubmit}
      errors={errors}
      formFields={
        <>
          {filterKeys.includes("accounts") &&
            accountOptions &&
            accountOptions.length > 1 && (
              <APAutoComplete
                name={"accounts"}
                label="Employers"
                options={accountOptions}
                control={control}
                validations={{ required: true }}
                errors={errors}
                defaultValue={accountOptions}
                setValue={setValue}
                multiple
              />
            )}

          {filterKeys.includes("documentTypes") && documentTypes.length > 1 && (
            <APAutoComplete
              name={"documentTypes"}
              label="Document Types"
              options={documentOptions}
              control={control}
              validations={{ required: true }}
              errors={errors}
              defaultValue={documentOptions}
              setValue={setValue}
              multiple
            />
          )}

          {filterKeys.includes("dateFrom") && (
            <Box
              sx={{
                display: "flex",
                columnGap: 2,
              }}
            >
              <APDateField
                name="dateFrom"
                label={buildFieldLabel("date", "From")}
                control={control}
                defaultValue={null}
                errors={errors}
                setError={setError}
                clearErrors={clearErrors}
                maxDate={dateTo ? dayjs(dateTo) : undefined}
                clearable
              />
              {filterKeys.includes("dateTo") && (
                <APDateField
                  name="dateTo"
                  label={buildFieldLabel("date", "To")}
                  control={control}
                  defaultValue={null}
                  errors={errors}
                  setError={setError}
                  clearErrors={clearErrors}
                  minDate={dateFrom ? dayjs(dateFrom) : undefined}
                  clearable
                />
              )}
            </Box>
          )}

          {filterKeys.includes("date2From") && (
            <Box
              sx={{
                display: "flex",
                columnGap: 2,
              }}
            >
              <APDateField
                name="date2From"
                label={buildFieldLabel("date2", "From")}
                control={control}
                defaultValue={null}
                errors={errors}
                setError={setError}
                clearErrors={clearErrors}
                maxDate={date2To ? dayjs(date2To) : undefined}
                clearable
              />
              {filterKeys.includes("date2To") && (
                <APDateField
                  name="date2To"
                  label={buildFieldLabel("date2", "To")}
                  control={control}
                  defaultValue={null}
                  errors={errors}
                  setError={setError}
                  clearErrors={clearErrors}
                  minDate={date2From ? dayjs(date2From) : undefined}
                  clearable
                />
              )}
            </Box>
          )}

          {filterKeys.includes("name") && (
            <APTextField
              name={"name"}
              label="Search"
              control={control}
              defaultValue=""
              validations={{}}
              errors={errors}
            />
          )}
        </>
      }
      renderItems={renderItems}
      isLoading={isLoading}
      isSuccess={isSuccess}
      topCollapseContent={topCollapseContent}
      bottomContent={
        <Stack
          spacing={1}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            pt: 2,
          }}
        >
          <BackButton />
          <RestrictedAppScope scope="external">
            <APContactHelpButton />
          </RestrictedAppScope>
        </Stack>
      }
      onPageLoad={updateAccountColorMap}
    />
  );
};

export default DocumentList;
